import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Code,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import {
  getListStaffUsageJobsQueryKey,
  listStaffUsageJobs,
} from "../../api/generated/endpoints/staff/staff";
import type { JobUsageView, UsageJob, UsageNames } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { formatCount, formatDateTime } from "../../ui/format";
import { JobCost, StatusBadge, TraceLink, formatUtcDay, keyLabel, typeName } from "./usage-format";
import { filtersOf, narrowed, rangeOf } from "./usage-search";
import classes from "./usage.module.css";

const PAGE = 50;

/** The names of every page loaded so far, as one. */
function namesOf(pages: { names: UsageNames }[]): UsageNames {
  const names: UsageNames = { users: {}, teams: {}, models: {} };
  for (const page of pages) {
    Object.assign(names.users, page.names.users);
    Object.assign(names.teams, page.names.teams);
    Object.assign(names.models, page.names.models);
  }
  return names;
}

/**
 * The generations behind one row of Usage (spec usage-analytics 3): newest start first, a page
 * at a time, open ones included, each with its run's credits and cost, and, with "Show steps",
 * its steps under it. No prompt shows here: a generation's content is in its trace alone,
 * which each row's start opens.
 */
export function StaffGenerationsScreen() {
  const search = useSearch({ from: "/signed-in/staff/usage/generations" });
  const navigate = useNavigate({ from: "/staff/usage/generations" });
  const { key, steps = false, ...view } = search;
  const by = view.by ?? "user";
  const params = { ...filtersOf(key === undefined ? view : narrowed(view, by, key)), steps };
  const jobs = useInfiniteQuery({
    queryKey: [...getListStaffUsageJobsQueryKey(params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listStaffUsageJobs(
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
  });
  const pages = jobs.data?.pages ?? [];
  const names = namesOf(pages);
  const items = pages.flatMap((page) => page.items);
  const { from, to } = rangeOf(key === undefined ? view : narrowed(view, by, key));
  // Opened from a user, every row is theirs.
  const showUser = by !== "user" || key === undefined;
  return (
    <Stack gap="md">
      <Anchor renderRoot={(anchor) => <Link {...anchor} to="/staff/usage" search={view} />}>
        ← Usage
      </Anchor>
      <Group justify="space-between" align="baseline">
        <Title order={2} size="h4">
          {key === undefined ? "Generations" : `Generations · ${keyLabel(by, key, names)}`}
        </Title>
        <Text size="sm" c="var(--cai-color-text-muted)">
          {formatUtcDay(from)} – {formatUtcDay(to)}
        </Text>
      </Group>
      <Checkbox
        label="Show steps"
        checked={steps}
        onChange={(event) =>
          void navigate({
            search: { ...search, steps: event.currentTarget.checked || undefined },
            replace: true,
          })
        }
      />
      {jobs.isPending ? <Loader size="sm" aria-label="Loading generations" /> : null}
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {jobs.isSuccess && items.length === 0 ? <Text>No generations match these filters.</Text> : null}
      {items.length === 0 ? null : (
        <Table.ScrollContainer minWidth={1200}>
          <Table style={{ opacity: jobs.isPlaceholderData ? 0.6 : 1 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Started</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Model</Table.Th>
                <Table.Th>Team · brand</Table.Th>
                {showUser ? <Table.Th>User</Table.Th> : null}
                <Table.Th className={classes.status}>Status</Table.Th>
                <Table.Th className={classes.num}>Outputs</Table.Th>
                <Table.Th className={classes.num}>Credits</Table.Th>
                <Table.Th className={classes.num}>Vendor cost</Table.Th>
                <Table.Th className={classes.num}>Steps</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.flatMap((job) => [
                <JobRow key={job.line.job_id} job={job} names={names} user={showUser} />,
                ...job.step_lines.map((line) => (
                  <JobRow
                    key={line.job_id}
                    job={stepOf(line)}
                    names={names}
                    user={showUser}
                    step
                  />
                )),
              ])}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {jobs.hasNextPage ? (
        <Group>
          <Button
            variant="light"
            loading={jobs.isFetchingNextPage}
            onClick={() => void jobs.fetchNextPage()}
          >
            Load more
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}

/** A step's row: its own line's credits and cost, none of its run's. */
const stepOf = (line: JobUsageView): UsageJob => ({
  line,
  steps: 0,
  charged: line.charged,
  refunded: line.refunded,
  vendor_cost_usd: line.vendor_cost_usd,
  vendor_cost_complete: line.vendor_cost_complete,
  step_lines: [],
});

/** Who started a job: their name and email, or no one for a system start. */
function UserCell({ id, names }: { id: string | null; names: UsageNames }) {
  if (id === null) return <Text c="var(--cai-color-text-muted)">No user</Text>;
  const user = names.users[id];
  if (user === undefined) return <Code>{id}</Code>;
  return (
    <Stack gap={0}>
      <Text>{user.name ?? user.email}</Text>
      {user.name === null ? null : (
        <Text size="xs" c="var(--cai-color-text-muted)">
          {user.email}
        </Text>
      )}
    </Stack>
  );
}

/**
 * A generation, or (`step`) one of its steps: the job's own line, and, for a generation, its
 * run's credits and cost, its steps' included.
 */
function JobRow(props: { job: UsageJob; names: UsageNames; user: boolean; step?: boolean }) {
  const { job, names, user, step = false } = props;
  const { line } = job;
  const open = line.status === "open";
  return (
    <Table.Tr className={step ? classes.step : undefined}>
      <Table.Td className={classes.nowrap}>
        {step ? "↳ " : null}
        <TraceLink teamId={line.team_id} jobId={line.job_id}>
          {formatDateTime(line.started_at)}
        </TraceLink>
      </Table.Td>
      <Table.Td>
        <Stack gap={0}>
          <Text className={classes.nowrap}>{typeName(line.job_type) ?? line.job_type}</Text>
          <Text size="xs" ff="monospace" c="var(--cai-color-text-muted)">
            {line.job_type}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <ModelCell line={line} names={names} />
      </Table.Td>
      <Table.Td>
        <Stack gap={0}>
          <Text>{names.teams[line.team_id] ?? line.team_id}</Text>
          {/* A brand is its id alone in v1: no staff read of a brand's name (spec 4). */}
          {line.brand_id === null ? null : (
            <Text size="xs" ff="monospace" c="var(--cai-color-text-muted)">
              {line.brand_id}
            </Text>
          )}
        </Stack>
      </Table.Td>
      {user ? (
        <Table.Td>
          <UserCell id={line.user_id} names={names} />
        </Table.Td>
      ) : null}
      <Table.Td className={classes.nowrap}>
        <StatusBadge status={line.status} />
      </Table.Td>
      <Table.Td className={classes.num}>
        {formatCount(line.delivered)} / {formatCount(line.outputs)}
      </Table.Td>
      <Table.Td className={classes.num}>
        <Credits job={job} held={open ? line.held : 0} />
      </Table.Td>
      <Table.Td className={classes.num}>
        <JobCost usd={job.vendor_cost_usd} complete={job.vendor_cost_complete} open={open} />
      </Table.Td>
      <Table.Td className={classes.num}>
        {job.steps === 0 ? <span className={classes.zero}>—</span> : formatCount(job.steps)}
      </Table.Td>
    </Table.Tr>
  );
}

function ModelCell({ line, names }: { line: JobUsageView; names: UsageNames }) {
  if (line.model_id === null) return <Text c="var(--cai-color-text-muted)">No model</Text>;
  return (
    <Stack gap={0}>
      <Text className={classes.nowrap}>{names.models[line.model_id] ?? line.model_id}</Text>
      {/* The adapter it ran on, once it settled. */}
      {line.vendor === null ? null : (
        <Text size="xs" c="var(--cai-color-text-muted)">
          {line.vendor}
        </Text>
      )}
    </Stack>
  );
}

/** The run's credits charged less refunded, and what its open job still holds. */
function Credits({ job, held }: { job: UsageJob; held: number }) {
  const net = job.charged - job.refunded;
  if (held > 0 && net === 0) {
    return <Text c="var(--cai-color-text-muted)">{formatCount(held)} held</Text>;
  }
  return (
    <Stack gap={2} align="flex-end">
      <Text>{formatCount(net)}</Text>
      {held > 0 ? <Text size="xs">+ {formatCount(held)} held</Text> : null}
      {job.refunded > 0 ? (
        <Badge size="xs" color="gray" variant="light">
          refunded {formatCount(job.refunded)}
        </Badge>
      ) : null}
    </Stack>
  );
}
